import {
  Combobox,
  Option,
  OptionGroup,
  Divider,
  Tooltip,
  useId,
  makeStyles,
  tokens,
} from "@fluentui/react-components";
import { useMemo, useState, useEffect } from "react";
import { ExtensionFolder } from "../Types";
import { useNavigateEv2Experiments } from "../Hooks/UseNavigateToEv2Experiments";
import { useAppContext } from "../Contexts";
import useAppInsights from "../Hooks/UseAppInsights";
import { NavType } from "../Shared/AppInsights";

interface ExtensionsComboBoxProps {
  selected?: string;
  extensions: ExtensionFolder[];
}

// Helper component for options with ellipsis and tooltip
const OptionWithTooltip: React.FC<{
  text: string;
  value: string;
}> = ({ text, value }) => {
  const styles = useStyles();

  return (
    <Option key={value} value={value} text={text}>
      <Tooltip content={text} relationship="description">
        <div className={styles.optionText}>{text}</div>
      </Tooltip>
    </Option>
  );
};

const ExtensionsComboBox: React.FC<ExtensionsComboBoxProps> = ({
  selected: initialSelection = "",
  extensions,
}) => {
  const { navigateToExperiment } = useNavigateEv2Experiments();
  const { trackNavigationEvent, trackOperationEvent } =
    useAppInsights("ExtensionsCombobox");
  const {
    setExtensionName: setExtensionNameContext,
    getNormalizedExtensionName,
  } = useAppContext();
  const comboId = useId("extensions-combo");
  const styles = useStyles();
  const [inputValue, setInputValue] = useState(initialSelection);

  useEffect(() => {
    if (initialSelection && extensions) {
      const normalizedExtensionName =
        getNormalizedExtensionName(initialSelection);
      const isValid = extensions.find(
        (extension) => extension.name === normalizedExtensionName,
      );
      if (isValid) {
        setInputValue(normalizedExtensionName);
        setExtensionNameContext(normalizedExtensionName);
      }
    }
  }, [
    initialSelection,
    extensions,
    getNormalizedExtensionName,
    setExtensionNameContext,
  ]);

  const sortedExtensions = [...extensions].sort((a, b) =>
    a.extensionRepoPath.localeCompare(b.extensionRepoPath),
  );

  const { standaloneExtensions, groupedExtensions } = useMemo(() => {
    const processedPaths = new Set<string>();
    const standalone: ExtensionFolder[] = [];
    const grouped: Array<{
      parent: ExtensionFolder;
      children: ExtensionFolder[];
    }> = [];

    // Check if the current input value exactly matches an existing extension
    const isExactMatch = sortedExtensions.some(
      (ext) => ext.name === inputValue,
    );

    // If exact match or empty input, show all extensions; otherwise filter
    const filterText = inputValue.toLowerCase();
    const shouldFilter = !isExactMatch && inputValue.trim();
    const filteredExtensions = shouldFilter
      ? sortedExtensions.filter((ext) =>
          ext.name.toLowerCase().includes(filterText),
        )
      : sortedExtensions;

    filteredExtensions.forEach((extension) => {
      // Skip if already processed as a child
      if (processedPaths.has(extension.extensionRepoPath)) {
        return;
      }

      // Find all children of this extension (that also match the filter)
      const children = filteredExtensions.filter((child) =>
        child.extensionRepoPath.startsWith(extension.extensionRepoPath + "/"),
      );

      if (children.length > 0) {
        // Parent with children - create a group
        grouped.push({ parent: extension, children });
        // Mark all children as processed
        children.forEach((child) =>
          processedPaths.add(child.extensionRepoPath),
        );
      } else {
        // Standalone extension
        standalone.push(extension);
      }
      processedPaths.add(extension.extensionRepoPath);
    });

    return {
      standaloneExtensions: standalone,
      groupedExtensions: grouped,
      isFiltering: shouldFilter,
    };
  }, [sortedExtensions, inputValue]);

  const handleOptionSelect = (event: any, data: any) => {
    const selected = data.optionValue;
    if (selected) {
      setInputValue(selected);
      const destination = navigateToExperiment(selected);
      trackNavigationEvent({
        destination,
        function: "handleOptionSelect",
        navType: NavType.Internal,
      });
      if (selected) {
        setExtensionNameContext(selected);
      }
    }
  };

  const handleInputChange = (event: React.FormEvent<HTMLInputElement>) => {
    setInputValue(event.currentTarget.value);
    trackOperationEvent({
      operation: "SelectExtension",
      function: "handleInputChange",
      properties: { selectedExtension: event.currentTarget.value },
    });
  };

  return (
    <Combobox
      className={styles.combobox}
      aria-labelledby={comboId}
      placeholder="Select extension..."
      value={inputValue}
      onOptionSelect={handleOptionSelect}
      onInput={handleInputChange}
      freeform
      autoComplete="both"
    >
      <div className={styles.comboboxContainer}>
        {standaloneExtensions.map((extension) => (
          <OptionWithTooltip
            key={extension.name}
            text={extension.name}
            value={extension.name}
          />
        ))}

        {standaloneExtensions.length > 0 && groupedExtensions.length > 0 && (
          <Divider />
        )}

        {groupedExtensions.map(({ parent, children }) => (
          <OptionGroup key={parent.name} label={parent.name}>
            {children.map((child) => (
              <OptionWithTooltip
                key={child.name}
                text={child.name}
                value={child.name}
              />
            ))}
          </OptionGroup>
        ))}
      </div>
    </Combobox>
  );
};

const useStyles = makeStyles({
  comboboxContainer: {
    maxHeight: "400px",
  },
  combobox: {
    width: "200px",
    // Ensure dropdown is wide enough to show content but still respects max-width
    "& .fui-Listbox": {
      minWidth: "200px",
      maxWidth: "500px",
    },
    // Disable browser autocomplete and spell checking
    "& input": {
      spellCheck: "false",
    },
  },
  optionText: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    maxWidth: "100%",
    display: "block",
    // Add some padding to prevent text from touching edges
    paddingRight: tokens.spacingHorizontalXS,
  },
});

export default ExtensionsComboBox;
